import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { 
  Box, 
  Chip,
  CircularProgress,  
  IconButton,
  Table, 
  TableBody, 
  TableCell, 
  TableFooter, 
  TableHead, 
  TablePagination, 
  TableRow, 
  TableSortLabel, 
  Tooltip,
  Typography 
} from '@mui/material';

import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import TablePaginationActions from '../../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../../components/hooks/useTablePaginationInit';
import TableTimeStamps from '../../../../components/utils/TableTimeStamps';
import { TILE_ACCENT } from '../../../../components/utils/SummaryTiles';
import Copyable from '../../../../components/utils/Copyable';
import TopBar from '../../../../components/utils/TopBar';
import NA from '../../../../components/utils/NA';

import CodeViewModal from '../../../../components/modals/CodeViewModal';

import {
  SLOW_THRESHOLD_MS,
  formatBytes,
  formatDuration,
  latencyAccent,
  readActivity
} from './activityInsights';

import { 
	GetActivitiesBIZfusion 
} from '../../../../redux/actions/business/fusion/activitiesActions';

const ActivitiesPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.fusion"]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");

  // Both filters are resolved by the backend, so the pagination count below
  // is the count for the whole table, not just the rows on screen.
  const [outcomeFilter, setOutcomeFilter] = useState("");
  const [slowOnly, setSlowOnly] = useState(false);

  const [codeViewModalOpen, setCodeViewModalOpen] = useState(false);
  const [codeView, setCodeView] = useState({ title: "", content: "" });

	const [activitiesList, setActivitiesList] = useState([]);
	const [activitiesTotal, setActivitiesTotal] = useState(0);

  const [
    page, 
    rowsPerPage, 
    handleChangePage, 
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  // `sort` is passed straight to the backend as an ORDER BY column, so only
  // real columns may carry a sort label — Outcome, Summary, Payload and
  // Latency are derived in the browser and have none.
  const tableHeadingsList = [
    {
      label: "Id",
      sort: "id",
    },
    {
      label: "Outcome",
      sort: null,
    },
    {
      label: "Summary",
      sort: null,
    },
    {
      label: "Payload",
      sort: null,
    },
    {
      label: "Latency",
      sort: null,
    }, 
    {
      label: "Track Id",
      sort: "track_id",
    },
    {
      label: "Time stamps",
      sort: "createdAt",
    }
  ];

  const handleSort = (value) => {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    setSort(value);
  };

  // Any filter change invalidates the current offset — reset to the first page
  // in the same update so only one fetch goes out.
  const applyFilter = (apply) => {
    handleChangePage(null, 0);
    apply();
  };

  const openPayload = (title, content) => {
    setCodeView({ title: title, content: content });
    setCodeViewModalOpen(true);
  };

  useEffect(() => {
		HandleGetActivities();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection, outcomeFilter, slowOnly]);

  useEffect(() => {
    try {
      formatActivitiesList(state.activitiesList.records || []);
      setActivitiesTotal(state.activitiesList.total || 0);
    } catch (err) {}
  }, [state]);

  const formatActivitiesList = (list) => {
    // Parsed once here rather than per cell — a page of rows can carry a few
    // hundred KB of JSON and every column needs something out of it.
    setActivitiesList(list.map((row) => ({ ...row, insight: readActivity(row) })));
  };

	async function HandleGetActivities(customLoading) {
		let query = ""
    if(search) query += `&search=${encodeURIComponent(search)}`;
    if(sort) query += `&field=${sort}`;
    if(sortDirection) query += `&order=${sortDirection}`;
    if(outcomeFilter) query += `&outcome=${outcomeFilter}`;
    if(slowOnly) query += `&slow_ms=${SLOW_THRESHOLD_MS}`;
    dispatch(GetActivitiesBIZfusion(page+1, rowsPerPage, query, customLoading ? customLoading : setLoading));
	};

  return (
    <Box>

      <CodeViewModal
        open={codeViewModalOpen}
        setOpen={setCodeViewModalOpen}
        title={codeView.title}
        language={"json"}
        content={codeView.content}
        />

      <TopBar
        title={"Activities"}
        search={search}
        searchPlaceholder={"Search message, error, payload, track id"}
        onSearchChange={(e) => {
          applyFilter(() => setSearch(e.target.value));
        }}
        />

      <Box 
        className="flexCenterSBRow" 
        gap="15px"
        mb={"10px"}>
        <Box  
          className="flexCenter_Row"
          gap="15px">
          <Chip 
            color={outcomeFilter === "" ? 'info' : 'default'}
            label="All"
            onClick={() => {
              applyFilter(() => setOutcomeFilter(""));
            }}
            />
          <Chip 
            color={outcomeFilter === "success" ? 'success' : 'default'}
            label="Succeeded"
            onClick={() => {
              applyFilter(() => setOutcomeFilter("success"));
            }}
            />
          <Chip 
            color={outcomeFilter === "failed" ? 'error' : 'default'}
            label="Failed"
            onClick={() => {
              applyFilter(() => setOutcomeFilter("failed"));
            }}
            />
          <Chip 
            variant={slowOnly ? 'filled' : 'outlined'}
            color={slowOnly ? 'warning' : 'default'}
            label={`Slower than ${SLOW_THRESHOLD_MS / 1000}s`}
            onClick={() => {
              applyFilter(() => setSlowOnly(!slowOnly));
            }}
            />
        </Box>

        <Typography variant='caption' color={"grey"}>
          {activitiesTotal} matching {activitiesTotal === 1 ? "activity" : "activities"}
        </Typography>
      </Box>

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          activitiesList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No activity found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell
                      key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell
                      key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort ? true : false}
                        direction={sortDirection}
                        onClick={() => {
                          handleSort(heading.sort);
                        }}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                activitiesList.map((row, index) => (
                  <TableRow 
                    key={index}>
                    <TableCell>
                      <Typography
                        variant='caption'
                        color={"grey"}>
												<Copyable>
                        	{row.id}
												</Copyable>
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Chip
                        size='small'
                        label={row.insight.outcome.label}
                        sx={{
                          color: row.insight.outcome.accent,
                          border: `1px solid ${row.insight.outcome.accent}60`,
                          background: `${row.insight.outcome.accent}14`,
                          fontWeight: 600,
                        }}
                        />
                      <Typography
                        variant='caption'
                        display={"block"}
                        color={"grey"}
                        mt={"4px"}>
                        {row.stage || <NA />}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ maxWidth: "340px" }}>
                      <Typography variant='body2'>
                        {row.insight.message || <NA />}
                      </Typography>
                      {
                        row.insight.error
                        &&
                        <Tooltip title={row.insight.error}>
                          <Typography
                            variant='caption'
                            display={"block"}
                            mt={"2px"}
                            sx={{
                              color: TILE_ACCENT.bad,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}>
                            {row.insight.error}
                          </Typography>
                        </Tooltip>
                      }
                    </TableCell>

                    <TableCell>
                      <Box className='flexCenter_Row' gap={"4px"}>
                        {
                          row.insight.shape
                          &&
                          <Typography
                            variant='caption'
                            sx={{ fontFamily: "monospace", color: TILE_ACCENT.info }}>
                            {row.insight.shape}
                          </Typography>
                        }
                        <Tooltip title={row.insight.reqEmpty ? "Request body (empty)" : "Request body"}>
                          <span>
                            <IconButton
                              size='small'
                              disabled={row.insight.reqEmpty}
                              onClick={() => {
                                openPayload(`Request · activity ${row.id}`, row.insight.reqPretty);
                              }}>
                              <VisibilityOutlinedIcon
                                fontSize='small'
                                color={row.insight.reqEmpty ? 'disabled' : 'primary'} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={"Response body"}>
                          <span>
                            <IconButton
                              size='small'
                              disabled={row.insight.resEmpty}
                              onClick={() => {
                                openPayload(`Response · activity ${row.id}`, row.insight.resPretty);
                              }}>
                              <VisibilityOutlinedIcon
                                fontSize='small'
                                color={row.insight.resEmpty ? 'disabled' : 'primary'} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        req {formatBytes(row.insight.reqSize)} · res {formatBytes(row.insight.resSize)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Tooltip title={"Time between the request being logged and the response being saved"}>
                        <Typography
                          variant='body2'
                          sx={{ color: latencyAccent(row.insight.latencyMs), whiteSpace: "nowrap" }}>
                          {formatDuration(row.insight.latencyMs) || <NA />}
                        </Typography>
                      </Tooltip>
                    </TableCell>

										<TableCell>
                      <Typography variant='caption'>
                        <Copyable>
                          {row.track_id || <NA />}
                        </Copyable>
                      </Typography>
										</TableCell>

                    <TableCell>
                      <TableTimeStamps
                        createdAt={row.createdAt}
                        updatedAt={row.updatedAt}
                        />
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                   rowsPerPageOptions={[5, 10, 25, 50, 100]} 
                  colSpan={10}
                  count={activitiesTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page'
                    },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                  />
              </TableRow>
            </TableFooter>
          </Table>
      }
    </Box>
  )
}

export default ActivitiesPage;
